import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { socialApi } from "@bw/shared/api";
import { scheduleFeedReconciliation } from "@bw/shared/hooks";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * The friend lifecycle mutations the FriendsPage exposes:
 *   - accept   (an INCOMING pending request) → acceptFriendRequest(friendship_id)
 *   - decline  (an INCOMING pending request) → declineFriendRequest(friendship_id)
 *   - cancel   (an OUTGOING pending request, the SENDER withdraws) →
 *              cancelFriendRequest(friendship_id)
 *   - unfriend (an ACCEPTED friend) → unfriend(user_id) — the OTHER user's id,
 *              NOT a friendship id.
 *   - sendRequest (Slice 4b, find-players) → sendFriendRequest(user_id) — the
 *              RECIPIENT's user_id from a search result. Its error is DELIBERATELY
 *              ISOLATED from the four inbox verbs (its own `sendRequest.error`,
 *              its own onMutate reset cycle) so a duplicate-request failure on the
 *              Add-a-friend section never pollutes — and is never cleared by — the
 *              inbox's combined accept/decline/cancel/unfriend banner. On success
 *              it invalidates friendRequests (the new outgoing request appears in
 *              the inbox AND the searched row flips to "Requested" on the refetch).
 *              NO optimistic flip — the row only shows "Requested" once the
 *              refetched outgoing list contains the recipient's user_id.
 *
 * On success each INVALIDATES surgically so the page reflects the SERVER's truth:
 *   - accept   → friendRequests (the row leaves the inbox) + friends (the new
 *                friend appears)
 *   - decline  → friendRequests (the row leaves the inbox)
 *   - cancel   → friendRequests (the row leaves outgoing)
 *   - unfriend → friends (the row leaves the list)
 *
 * NO optimistic update: the cache is never mutated before the server confirms, so
 * a rejected action (e.g. cancel on a no-longer-pending request → 404) leaves the
 * row exactly in place and the error surfaces on `mutation.error` for the page to
 * show honestly — never a phantom removal.
 *
 * Stale-error honesty (mirrors useChallengeActions): react-query never auto-resets
 * a SETTLED mutation, so a failed action's error would otherwise outlive a later
 * SUCCESSFUL one and keep a lying banner up. Each mutation's `onMutate` resets the
 * OTHER three before it runs, so the page's combined-error banner reflects only
 * the action currently in flight / most recently settled.
 *
 * Re-entrancy is the page's concern: it disables each button while `isPending` so
 * a double-click fires the mutation once.
 */
export function useFriendActions() {
  const queryClient = useQueryClient();

  const invalidateRequests = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.social.friendRequests(),
    });
  const invalidateFriends = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.social.friends(),
    });
  const reconcileFriendFeeds = () =>
    scheduleFeedReconciliation(queryClient, [
      { scope: "home" },
      { scope: "friends" },
    ]);

  // Refs hold the live `reset` of each mutation so a sibling's onMutate always
  // clears the CURRENT settled state — never a stale closure from an old render.
  const resetAccept = useRef<() => void>(() => {});
  const resetDecline = useRef<() => void>(() => {});
  const resetCancel = useRef<() => void>(() => {});
  const resetUnfriend = useRef<() => void>(() => {});
  // Slice F3: the Message (createConversation) mutation's error is folded into the
  // SAME shared actionError banner the four inbox verbs feed, so it joins this
  // reset cycle — a settled create-failure must not outlive a later successful
  // accept/decline/cancel/unfriend. The page wires this ref to
  // createConversation.reset (it lives in a separate hook); it defaults to a no-op
  // so useFriendActions stays usable standalone.
  const resetCreate = useRef<() => void>(() => {});

  const accept = useMutation<unknown, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (friendshipId: string) =>
      socialApi.acceptFriendRequest(friendshipId),
    onMutate: () => {
      resetDecline.current();
      resetCancel.current();
      resetUnfriend.current();
      resetCreate.current();
    },
    onSuccess: () => {
      // Accepting moves a row out of the inbox AND adds a new friend to the list.
      invalidateRequests();
      invalidateFriends();
      reconcileFriendFeeds();
    },
  });

  const decline = useMutation<void, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (friendshipId: string) =>
      socialApi.declineFriendRequest(friendshipId),
    onMutate: () => {
      resetAccept.current();
      resetCancel.current();
      resetUnfriend.current();
      resetCreate.current();
    },
    onSuccess: () => invalidateRequests(),
  });

  const cancel = useMutation<void, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (friendshipId: string) =>
      socialApi.cancelFriendRequest(friendshipId),
    onMutate: () => {
      resetAccept.current();
      resetDecline.current();
      resetUnfriend.current();
      resetCreate.current();
    },
    onSuccess: () => invalidateRequests(),
  });

  const unfriend = useMutation<void, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (otherUserId: string) => socialApi.unfriend(otherUserId),
    onMutate: () => {
      resetAccept.current();
      resetDecline.current();
      resetCancel.current();
      resetCreate.current();
    },
    onSuccess: () => {
      invalidateFriends();
      reconcileFriendFeeds();
    },
  });

  // Slice 4b send-request. DELIBERATELY NOT part of the four-way reset cycle
  // above: it keeps its OWN error so the Add-a-friend section's failure banner is
  // independent of the inbox banner (a duplicate-request 409 here never clears,
  // or is cleared by, an accept/decline/cancel/unfriend error). On success it
  // invalidates friendRequests so the new outgoing request surfaces in the inbox
  // AND the searched row flips to "Requested" on the refetch — never optimistic.
  const sendRequest = useMutation<unknown, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (toUserId: string) => socialApi.sendFriendRequest(toUserId),
    onSuccess: () => invalidateRequests(),
  });

  resetAccept.current = accept.reset;
  resetDecline.current = decline.reset;
  resetCancel.current = cancel.reset;
  resetUnfriend.current = unfriend.reset;

  /**
   * Reset the four inbox verbs' settled state. The page passes this as the Message
   * (createConversation) hook's `onActionStart`, so STARTING a Message clears any
   * settled accept/decline/cancel/unfriend error from the shared banner — the
   * symmetric half of `resetCreate` (which the inbox verbs call to clear a settled
   * create-error). Together they keep the combined banner reflecting only the
   * action currently in flight / most recently settled, even though createConversation
   * lives in a separate hook.
   */
  const resetInboxErrors = (): void => {
    resetAccept.current();
    resetDecline.current();
    resetCancel.current();
    resetUnfriend.current();
  };

  /**
   * True while ANY of this row's request actions (accept / decline / cancel) is in
   * flight against THIS friendship_id. The page freezes the WHOLE row (both Accept
   * AND Decline on an incoming row) while one is pending, so a slow accept can't
   * leave the sibling decline fireable — a same-row accept+decline race. Scoped to
   * the active friendship_id via `mutation.variables`, so a pending action on row A
   * never freezes row B (a different friendship_id).
   */
  const isRequestBusy = (friendshipId: string): boolean =>
    (accept.isPending && accept.variables === friendshipId) ||
    (decline.isPending && decline.variables === friendshipId) ||
    (cancel.isPending && cancel.variables === friendshipId);

  /**
   * True while the unfriend action is in flight against THIS user_id. Scoped to the
   * active user_id so a pending unfriend on friend A never freezes friend B.
   */
  const isFriendBusy = (userId: string): boolean =>
    unfriend.isPending && unfriend.variables === userId;

  /**
   * True while a send-request is in flight against THIS recipient user_id. Scoped
   * to the active user_id via `mutation.variables` so the page can disable only
   * the Add button on the row being added — a double-click fires sendRequest once,
   * and a pending Add on row A never freezes row B.
   */
  const isSendBusy = (userId: string): boolean =>
    sendRequest.isPending && sendRequest.variables === userId;

  return {
    accept,
    decline,
    cancel,
    unfriend,
    sendRequest,
    isRequestBusy,
    isFriendBusy,
    isSendBusy,
    // Slice F3 cross-hook reset wiring (the create-conversation error shares the
    // inbox banner): the page sets `resetCreate.current = createConversation.reset`
    // so the inbox verbs clear a settled create-error, and passes `resetInboxErrors`
    // as createConversation's onActionStart so a starting Message clears a settled
    // inbox error. See FriendsPage.
    resetCreate,
    resetInboxErrors,
  };
}
